import { ErrorMessage, Formik } from 'formik';
import React, { useState } from 'react';
import classNames from 'classnames';
import { createConversation } from '../../utils/api/chatApiUtil';
import { getBookUrlFromDOM } from '../../utils/book';
import { getBaseUrl } from '../../utils/config';
import GenericModal from '../ibok-core/module/GenericModal';
import UserFeedback from '../UserFeedback';

import './index.less';
import ContactBookPreview from './ContactBookPreview';

const mapApiErrorToUserMessage = (error) => {
  if (!error.data) {
    console.error('Unknown error from api', error);
    return 'Noe gikk galt og vi kunne ikke kontakte selgeren';
  }

  const { data } = error;

  if (data.status >= 500) {
    console.error(`Server error from chat api. Actual message ${data.title}`, error);
    return 'Noe gikk galt med forespørselen. Forsøk gjerne igjen senere';
  }

  const title = data.title.toLowerCase();

  if (title === 'classified does not exist') {
    return 'Denne annonsen eksisterer visst ikke lenger';
  }

  if (title.includes('conversation between users already exists')) {
    return 'Du har allerede en samtale med denne personen';
  }

  return 'Noe gikk galt med forespørselen. Forsøk gjerne igjen senere';
};

const validateForm = (values) => {
  const errors = {};

  if (!values.message) {
    errors.message = 'Meldingen er for kort';
  }

  if (values.message.trim().length < 1) {
    errors.message = 'Meldingen må inneholde bokstaver';
  }

  if (values.message.length < 20) {
    errors.message = `Meldingen er for kort. Den må være minst 20 tegn, din er ${values.message.length} tegn`;
  }

  if (values.message.length > 999) {
    errors.message = `Meldingen er for lang. Den kan være maks 999 tegn, din er ${values.message.length} tegn`;
  }

  return errors;
};

const hasRelatedConversation = (classified) => !!classified.conversation;

const hasErrorInField = (formProps, field) => !!formProps.errors[field] && formProps.touched[field];

const ContactSellerModal = (props) => {
  const {
    classified,
    coverUrl: explicitCoverUrl = null,
    onConversationCreated,
    onClose,
  } = props;
  const [createdConversation, setConversation] = useState(null);

  const onSubmit = (values, { setStatus, setSubmitting }) => {
    createConversation(values.message, classified)
      .then((conversation) => {
        setConversation(conversation);
        onConversationCreated(conversation);
        setSubmitting(false);
        // Ensure any statuses is cleared out
        setStatus({});
      })
      .catch((err) => {
        const message = mapApiErrorToUserMessage(err);
        setStatus({ apiError: message });
        setSubmitting(false);
      });
  };

  const coverUrl = explicitCoverUrl || getBookUrlFromDOM(classified.bookId);

  const hasConversation = hasRelatedConversation(classified);
  const { conversation = null } = classified;

  return <GenericModal title="Start samtale" subtitle={<a href={`selger/${classified.userId}`}>Sjekk selgerens andre annonser</a>} onDismiss={onClose}>
    <ContactBookPreview classified={classified} coverUrl={coverUrl}/>

    {hasConversation
      ? <section>
        <UserFeedback status="info" showIcon={false}>
          <h3>Du har vært i kontakt med denne selgeren tidligere</h3>
          <p>
            Gå til denne samtalen for å spørre etter boken
          </p>
          <p>
            <a
              className="button has-fas"
              href={`${getBaseUrl()}chat/${conversation.slug}?interested_in=${classified.id}`}
            >
              <span>Gå til samtalen</span>
              <i className="fas fa-arrow-right"/>
            </a>
          </p>
        </UserFeedback>

        <button className="has-fullwidth plain" onClick={onClose}>Lukk skjema</button>
      </section>

      : <Formik
        initialValues={{ message: '' }}
        validate={validateForm}
        onSubmit={onSubmit}
        validateOnBlur={false}
      >
        {(formProps) => <form
          onSubmit={formProps.handleSubmit}
          className="vertical contactsellerform"
        >
          {formProps.status && formProps.status.apiError && <UserFeedback status="error">
            <h4>Klarte ikke å sende meldingen</h4>
            <p>{formProps.status.apiError}</p>
          </UserFeedback>}

          {createdConversation && <UserFeedback status="success">
            <h4>Selger er kontaktet</h4>
            <p><a href={`${getBaseUrl()}chat/${createdConversation.slug}`}>Gå til samtalen</a></p>
          </UserFeedback>}

          <label htmlFor="message">Melding*</label>
          <textarea
            name="message"
            id="message"
            cols="30"
            rows="10"
            onChange={formProps.handleChange}
            onBlur={formProps.handleBlur}
            autoFocus={true}
            className={classNames('has-grey-background', { 'has-error': hasErrorInField(formProps, 'message') })}
            placeholder="Hei, jeg ønsker å kjøpe boken om den fortsatt er tilgjengelig"
          />
          <ErrorMessage name="message" component="p" className="form-error"/>

          <div className="columns is-reversed">
            {!createdConversation
              ? <>
                <button type="submit"
                        className="button"
                        disabled={formProps.isSubmitting}
                >{formProps.isSubmitting ? 'Sender...' : 'Ta kontakt'}</button>
                <button type="button" className="button plain" onClick={onClose}>Avbryt</button>
              </>
              : <button type="button" onClick={onClose}>Lukk skjema</button>}
          </div>
        </form>}
      </Formik>}
  </GenericModal>;
};

export default ContactSellerModal;
